import { useContext, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/browser'

import { APP_ID, APP_VERSION, VISITOR_ID_KEY } from 'app-constants'
import trackEvent from 'utils/analytics/trackEvent'
import { setAuthItems } from 'utils/auth'
import { sendToSentry } from 'utils/errors'
import { Flow, FlowRoute } from 'utils/FlowProvider/types'
import { getGeneralError } from 'utils/getGeneralError'

import { datadogRum } from '@datadog/browser-rum'
import { isClickId } from './queryParams'
import { v4 } from 'uuid'
import Environment from 'utils/environment'
import { useCreateUserMutation } from 'generated/types'
import { useApolloErrorModal } from '@acorns/web-utils'
import { errorModal, generateErrorCodeMap } from './errors'

import { UserContext } from '../../../providers/UserProvider/index'
import { getSourceAttributes } from 'utils/sourceAttributes'
import { AcornsEarlyContext } from '../../../providers/AcornsEarlyProvider'

declare global {
  interface Window {
    udid: string
  }
}
const generateUDID = (env: Environment, visitorID: string) => {
  if (env.get('testing')) {
    return '784e5cd1-032a-4cb5-82db-5816ee5d7af0'
  }

  return visitorID ?? v4()
}

export const useSignupFormSubmit = () => {
  const location = useLocation()
  const [createUser] = useCreateUserMutation()
  const { closeErrorModal, showErrorModal } = useApolloErrorModal()
  const { setUserData } = useContext(UserContext)
  const { saveEarlyCustomerInfo } = useContext(AcornsEarlyContext)

  const handleAnalytics = (input: {
    partnerName: string
    flow: Flow
    sourceId: string
    uuid: string
  }) => {
    // Identify the user for subsequent tracking in interior pages
    window.analytics.identify(input.uuid, {
      app_version: APP_VERSION,
      partner: input.partnerName,
      funnel_type: input.flow,
      source_id: input.sourceId,
      onboarding_app_id: APP_ID,
    })

    // Identify the user for error tracking
    Sentry.configureScope((scope) => {
      scope.setUser({ id: input.uuid })
    })

    trackEvent('Funnel Started', {
      eventData: {
        event: 'funnel_started',
        event_text: 'Funnel Started',
      },
      location,
      flow: input.flow,
    })
  }

  const handleError = ({
    error,
    env,
    values,
    setSubmitting,
    submitForm,
  }: {
    error: any
    env: Environment
    values: any
    setSubmitting: (submitting: boolean) => void
    submitForm: () => void
  }) => {
    let errorCode

    if (error.graphQLErrors) {
      // Obtain error code from GraphQL response
      errorCode = error?.graphQLErrors?.[0]?.extensions?.response?.body?.errors?.[0] ?? null
    } else {
      // Obtain error from REST response
      errorCode = error?.response?.data?.errors?.[0] ?? null
    }

    const errorCodeMap = generateErrorCodeMap({ env, closeErrorModal }, values)

    // This is for handling when backend sends us an error where captcha is required.
    // We don't want to rely on using a toggle to check backend because our client should
    // not be concerned with backend toggles.

    /* istanbul ignore else */
    if (errorCode && errorCode.toLowerCase().includes('captcha failed')) {
      setSubmitting(false)
      return
    }

    if (errorCode && errorCodeMap.get(errorCode)) {
      const { title, message, actions } = errorCodeMap.get(errorCode)
      showErrorModal(
        errorModal({
          title,
          message,
          actions,
          errorCode,
        }),
      )
    } else {
      const retryHandler = () => {
        closeErrorModal()
        submitForm()
      }
      const { title, message, actions, image } = getGeneralError(
        error,
        { closeErrorModal },
        retryHandler,
      )
      showErrorModal(
        errorModal({
          title,
          message,
          actions,
          errorCode,
          image,
        }),
      )
    }
    sendToSentry(error, { breadcrumb: 'Attempting to register a new user' })
  }

  const handleFormSubmit = useCallback(
    async ({ values, setSubmitting, getAgreementIds, env, flow, submitForm, route }) => {
      setSubmitting(true)
      // Add Sentry breadcrumb in case there is an error
      Sentry.addBreadcrumb({
        message: 'User is attempting to create a new account.',
      })

      // Prepare data to be used in call to create user
      const user = {
        email: values.email,
        password: values.password,
      }

      const sourceAttributes = getSourceAttributes()
      const partnerName = isClickId() ? 'impactradius' : null
      const agreementIds = getAgreementIds(partnerName)
      const VISITOR_ID = document.cookie
        .split(';')
        .filter((cookie) => cookie.includes(`${VISITOR_ID_KEY}`))
        .toString()
        .split('=')
        .pop()

      const data = {
        ...user,
        acceptanceDocumentVersionUuids: agreementIds,
        captchaToken: '',
        sourceAttributes: sourceAttributes.attributes,
        sourceId: sourceAttributes.id,
        udid: generateUDID(env, VISITOR_ID),
        usResident: true,
        withBasicAccount: false,
      }

      try {
        const { data: createUserData } = await createUser({ variables: data })

        const { refreshToken, token, uuid } = createUserData.createUser

        setAuthItems({
          identityId: uuid,
          token,
          refreshToken,
        })

        if (user.email) {
          setUserData({
            email: user.email,
          })
        }

        datadogRum.setGlobalContext({ userId: uuid })

        handleAnalytics({ partnerName, flow, sourceId: sourceAttributes.id, uuid })

        await saveEarlyCustomerInfo()

        route({ from: FlowRoute.Signup, action: 'continue' })
      } catch (error) {
        setSubmitting(false)

        handleError({
          error,
          env,
          values,
          setSubmitting,
          submitForm,
        })
      }
    },
    [],
  )

  return {
    handleFormSubmit,
  }
}
